/* eslint-disable @typescript-eslint/consistent-type-assertions */

import { and, type MachineImplementationsFrom, setup } from 'xstate5';

import {
  Actions,
  type Context,
  type Event,
  fetchCustomerDietaryPreferences,
  generateInitialContext,
  hydrateDietaryPreferences,
  rehydrateDietaryPreferences,
  updateRemotePreferences,
} from './setup';

import type { GuardPredicate } from 'xstate5/guards';

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    context: {} as Context,
    events: {} as Event,
    input: {} as {},
  },
  actions: {
    initialize: Actions.initialize,
    togglePreference: Actions.togglePreference,
    confirmPendingChanges: Actions.confirmPendingChanges,
    clearHydratedDietaryPreferences: Actions.clearHydratedDietaryPreferences,
    processRehydratedPreferences: Actions.processRehydratedPreferences,
    syncPreferences: Actions.syncPreferences,
    resetPendingChanges: Actions.resetPendingChanges,
    resetPreferences: Actions.resetPreferences,
    setActivateAfterFetch: Actions.setActivateAfterFetch,
    resetActivateAfterFetch: Actions.resetActivateAfterFetch,
    setStayActiveAfterConfirm: Actions.setStayActiveAfterConfirm,
    resetStayActiveAfterConfirm: Actions.resetStayActiveAfterConfirm,

    // ─── External Actions ────────────────────────────────────────────────

    onStart() {},
    onConfirm() {},
    onExternalUpdateFailure() {},
  },
  actors: {
    fetchCustomerDietaryPreferences,
    hydrateDietaryPreferences,
    rehydrateDietaryPreferences,
    updateRemotePreferences,
  },
  guards: {
    shouldRehydrate() {
      return false;
    },
    checkIfSignedIn() {
      return false;
    },
    canUpdateRemotePreferences() {
      return false;
    },
    shouldActivateAfterFetch({ context }) {
      return context.activateAfterFetch;
    },
    shouldStayActiveAfterConfirm({ context }) {
      return context.stayActiveAfterConfirm;
    },
  },
});

// ─── Machine ─────────────────────────────────────────────────────────────────

export const dietaryPreferencesMachine = model.createMachine({
  id: 'dietaryPreferencesMachine',
  context: generateInitialContext(),
  initial: 'waiting',
  states: {
    waiting: {
      on: {
        INITIALIZE: {
          target: 'initializing',
          actions: ['initialize'],
        },
      },
    },
    initializing: {
      always: [
        {
          target: 'rehydration',
          guard: 'shouldRehydrate',
        },
        {
          target: 'fetchingCustomerPreferences',
          guard: and(['canUpdateRemotePreferences', 'checkIfSignedIn']),
        },
        {
          target: 'idle',
        },
      ],
    },
    rehydration: {
      invoke: {
        src: 'rehydrateDietaryPreferences',
        onDone: [
          {
            target: 'fetchingCustomerPreferences',
            guard: and(['canUpdateRemotePreferences', 'checkIfSignedIn']),
            actions: [
              {
                type: 'processRehydratedPreferences',
                params: ({ event }) => event.output,
              },
            ],
          },
          {
            target: 'idle',
            actions: [
              {
                type: 'processRehydratedPreferences',
                params: ({ event }) => event.output,
              },
            ],
          },
        ],
        onError: {
          target: 'idle',
        },
      },
    },
    fetchingCustomerPreferences: {
      invoke: {
        src: 'fetchCustomerDietaryPreferences',
        input: (args) => ({
          preferences: args.context.preferences,
        }),
        onDone: [
          // If activateAfterFetch, sync + hydrate then go to active
          {
            target: 'hydrationBeforeActive',
            actions: [
              {
                type: 'syncPreferences',
                params: ({ event }) => ({
                  preferences: event.output,
                }),
              },
            ],
            guard: and(['shouldActivateAfterFetch', 'shouldRehydrate']),
          },
          {
            target: 'active',
            actions: [
              {
                type: 'syncPreferences',
                params: ({ event }) => ({
                  preferences: event.output,
                }),
              },
              'resetActivateAfterFetch',
            ],
            guard: 'shouldActivateAfterFetch',
          },
          // Original behavior: fetch was triggered explicitly or during init
          {
            target: 'hydration',
            actions: [
              {
                type: 'syncPreferences',
                params: ({ event }) => ({
                  preferences: event.output,
                }),
              },
            ],
            guard: 'shouldRehydrate',
          },
          {
            target: 'idle',
            actions: [
              {
                type: 'syncPreferences',
                params: ({ event }) => ({
                  preferences: event.output,
                }),
              },
            ],
          },
        ],
        onError: [
          {
            target: 'active',
            actions: ['resetActivateAfterFetch'],
            guard: 'shouldActivateAfterFetch',
          },
          {
            target: 'idle',
          },
        ],
      },
    },
    idle: {
      on: {
        START: [
          {
            target: 'fetchingCustomerPreferences',
            actions: ['onStart', 'setActivateAfterFetch', 'setStayActiveAfterConfirm'],
            guard: and(['canUpdateRemotePreferences', 'checkIfSignedIn']),
          },
          {
            target: 'active',
            actions: ['onStart', 'setStayActiveAfterConfirm'],
          },
        ],
        SYNC_PREFERENCES: {
          actions: [
            {
              type: 'syncPreferences',
              params: ({ event }) => ({ preferences: event.preferences }),
            },
          ],
        },
        FETCH_CUSTOMER_PREFERENCES: {
          target: 'fetchingCustomerPreferences',
          guard: and(['canUpdateRemotePreferences', 'checkIfSignedIn']),
        },
      },
    },
    active: {
      on: {
        TOGGLE_PREFERENCE: {
          actions: 'togglePreference',
        },
        CANCEL: {
          target: 'idle',
          actions: ['resetPendingChanges', 'resetStayActiveAfterConfirm'],
        },
        CONFIRM: [
          {
            target: 'updatingRemotePreferences',
            guard: and(['canUpdateRemotePreferences', 'checkIfSignedIn']),
          },
          {
            target: 'postConfirmation',
            actions: ['confirmPendingChanges'],
          },
        ],
      },
    },
    updatingRemotePreferences: {
      invoke: {
        src: 'updateRemotePreferences',
        input: (args) => ({
          pendingPreferences: args.context.pendingPreferences,
        }),
        onDone: {
          target: 'postConfirmation',
          actions: ['confirmPendingChanges'],
        },
        onError: {
          target: 'idle',
          actions: [
            'resetPendingChanges',
            'resetStayActiveAfterConfirm',
            'onExternalUpdateFailure',
          ],
        },
      },
    },
    postConfirmation: {
      entry: ['onConfirm'],
      always: [
        {
          target: 'hydrationAfterConfirm',
          guard: and(['shouldRehydrate', 'shouldStayActiveAfterConfirm']),
        },
        {
          target: 'hydration',
          guard: 'shouldRehydrate',
        },
        {
          target: 'active',
          guard: 'shouldStayActiveAfterConfirm',
        },
        {
          target: 'idle',
        },
      ],
    },
    hydration: {
      invoke: {
        src: 'hydrateDietaryPreferences',
        input: (args) => args.context.preferences,
        onDone: [
          {
            target: 'active',
            guard: 'shouldActivateAfterFetch',
            actions: ['resetActivateAfterFetch'],
          },
          {
            target: 'idle',
          },
        ],
        onError: {
          target: 'idle',
        },
      },
    },
    hydrationAfterConfirm: {
      invoke: {
        src: 'hydrateDietaryPreferences',
        input: (args) => args.context.preferences,
        onDone: {
          target: 'active',
        },
        onError: {
          target: 'active',
        },
      },
    },
    hydrationBeforeActive: {
      invoke: {
        src: 'hydrateDietaryPreferences',
        input: (args) => args.context.preferences,
        onDone: {
          target: 'active',
          actions: ['resetActivateAfterFetch'],
        },
        onError: {
          target: 'active',
          actions: ['resetActivateAfterFetch'],
        },
      },
    },
  },
  on: {
    RESET: {
      target: '.idle',
      actions: ['resetPreferences', 'clearHydratedDietaryPreferences'],
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type DietaryPreferencesMachineImplementations = MachineImplementationsFrom<
  typeof dietaryPreferencesMachine
>;
